import { z } from "zod";
import type { Locale } from "./common";
import type { F01ResultOutcome } from "./f01-result-page";
import { homeNowActionSchema } from "./home-now";

// TES-1657 (TES-1653 §1–3, §8, Emelie 2026-11-02) — the doctor's onboarding
// guidance in "Ditt medicinska team" (Meddelanden → the doctor's channel,
// Ö-1037). "Hem visar nästa uppgift och översikten, medan chatten
// introducerar uppgifterna, ger återkoppling och guidar vidare. Formulären
// öppnas från uppgiftskort och ligger fortfarande på egna ytor."
//
// Nothing here is stored. Every entry is derived, on each read, from the same
// facts Hem's cards read (F-01 answered, the latest F-01 outcome, the
// submitted health declaration, the assessment choice, the submitted F-07),
// so the channel and Hem share task and status by construction: "Hem och
// kanalen använder samma task_id, formulärinstans, deadline, ärende och
// status. … Inga dubbla ärenden, inskick eller notiser." The route sends no
// push.
//
// The kinds of entry, in the order the server sends them (oldest first):
//   message — a pre-programmed message under the fixed onboarding sender's
//             name (Ö-1044 ✅ 2026-11-02, "FAST ONBOARDINGAVSÄNDARE"): "En
//             verklig, namngiven och legitimerad Ella-läkare står som
//             avsändare för den förprogrammerade guidningen till alla nya
//             patienter". That role is not her case: it "innebär inte att
//             läkaren är tilldelad patientens medicinska ärende, har läst
//             svaren eller har påbörjat en individuell bedömning". Its `actions` are
//             the "DIREKTA HANDLINGAR EFTER MEDDELANDET" of the assessment
//             choice, present only while the choice is open.
//   task    — a compact task card: title, text, its status and the same
//             destination as Hem. "Påbörjad uppgift visar Fortsätt enligt
//             befintligt uppgiftsflöde; klar uppgift fälls ihop som Klart och
//             har ingen kvarhängande aktiv utförandehandling." A done card
//             therefore carries no actions.
//   link    — "Se din första kartläggning", to her saved F-01 entry in Din
//             resa (carebyella://journey/f01:<key>).
//   info    — an optional expandable box of the information group "Bra att
//             känna till" after the F-01 feedback: "Informationen är en
//             frivillig expanderbar ruta och inte ett obligatoriskt steg."
//             Only the approved "Så går läkarbedömningen till" (2026-11-02);
//             the other three texts are not final.
//   status  — a system-marked status, under no sender: the hand-over to the
//             doctor actually assigned to her case ("Nu är det {namn},
//             läkare på Ella, som går igenom ditt underlag och ansvarar för
//             din medicinska bedömning."). "Rena verifierade systemstatusar
//             är tydligt systemmärkta"; earlier messages keep their sender.
//   meeting — TES-1667: the compact card of her booked meeting, the same
//             meeting and remaining task as Hem's meeting card (below). Only
//             for a build at CLIENT_CONTRACT_CHANGES.meetingCard.
//
// No entry is a free-text field to the doctor (Ö-339 a): "Ingen fri
// patientinitierad skrivruta införs."
//
// Served as the optional `onboarding` of GET patient-api/v1/messages/doctor,
// only to a build at or after CLIENT_CONTRACT_CHANGES.onboardingChannel.

export const ONBOARDING_GUIDE_TASKS = [
  // "Din första kartläggning" — F-01 (carebyella://f01).
  "f01",
  // "Din hälsodeklaration" — F-02 (carebyella://health-declaration).
  "health_declaration",
  // "Uppgifter om din pågående behandling" — the transfer's prep page and
  // F-07 (carebyella://prescription-transfer).
  "transfer_details",
] as const;
export const onboardingGuideTaskSchema = z.enum(ONBOARDING_GUIDE_TASKS);
export type OnboardingGuideTask = z.infer<typeof onboardingGuideTaskSchema>;

export const onboardingGuideTaskStatusSchema = z.enum(["open", "done"]);
export type OnboardingGuideTaskStatus = z.infer<
  typeof onboardingGuideTaskStatusSchema
>;

const entryBase = {
  // Stable across fetches, unique in the list.
  id: z.string(),
  // ISO timestamp the entry belongs to; the list is sent oldest first.
  date: z.string(),
};

export const onboardingGuideMessageSchema = z.object({
  ...entryBase,
  kind: z.literal("message"),
  // The message's paragraphs, verbatim from the chat library with her first
  // name (and the doctor's) filled in.
  paragraphs: z.array(z.string().min(1)).min(1),
  // The assessment choice's two answers, performed in place exactly as on
  // Hem (choose_written_assessment / choose_care_meeting). Absent once she
  // has chosen, and on every other message.
  actions: z.array(homeNowActionSchema).optional(),
});
export type OnboardingGuideMessage = z.infer<
  typeof onboardingGuideMessageSchema
>;

export const onboardingGuideTaskEntrySchema = z.object({
  ...entryBase,
  kind: z.literal("task"),
  task: onboardingGuideTaskSchema,
  title: z.string().min(1),
  text: z.string().min(1),
  status: onboardingGuideTaskStatusSchema,
  // The card's action while it is open (one deep link, Hem's destination);
  // empty once it is done.
  actions: z.array(homeNowActionSchema),
});
export type OnboardingGuideTaskEntry = z.infer<
  typeof onboardingGuideTaskEntrySchema
>;

export const onboardingGuideLinkSchema = z.object({
  ...entryBase,
  kind: z.literal("link"),
  label: z.string().min(1),
  path: z.string().min(1),
});
export type OnboardingGuideLink = z.infer<typeof onboardingGuideLinkSchema>;

export const onboardingGuideInfoSchema = z.object({
  ...entryBase,
  kind: z.literal("info"),
  title: z.string().min(1),
  paragraphs: z.array(z.string().min(1)).min(1),
});
export type OnboardingGuideInfo = z.infer<typeof onboardingGuideInfoSchema>;

export const onboardingGuideStatusSchema = z.object({
  ...entryBase,
  kind: z.literal("status"),
  text: z.string().min(1),
});
export type OnboardingGuideStatus = z.infer<typeof onboardingGuideStatusSchema>;

// ---- the booked meeting (TES-1667) ------------------------------------------
//
// "KANONISK SYNK 2026-11-02 — BOKAT VÅRDMÖTE PÅ HEM OCH I DITT MEDICINSKA
// TEAM": "Hem och Ditt medicinska team visar samma mötesobjekt, samma
// obligatoriska uppgift, samma status och samma completion-villkor." The
// channel's card is "en kompakt variant av samma möteskort": the meeting Hem's
// card shows, with the task that remains before it, read by the same probe
// (apps/api/src/patient/onboarding-meeting.ts).

// What remains before the meeting, in the order Hem asks for it: "1. F-02
// återstår … 2. Annat obligatoriskt underlag för mötesvägen återstår,
// exempelvis F-07". Null once everything is in.
export const MEETING_ROUTE_TASKS = [
  "health_declaration",
  "transfer_details",
] as const;
export const meetingRouteTaskSchema = z.enum(MEETING_ROUTE_TASKS);
export type MeetingRouteTask = z.infer<typeof meetingRouteTaskSchema>;

export const onboardingGuideMeetingSchema = z.object({
  ...entryBase,
  kind: z.literal("meeting"),
  // The booked meeting, the same one as Hem's card (`meeting:<id>`) and
  // patient-api/v1/meetings/upcoming.
  meetingId: z.string(),
  // "Ditt vårdmöte".
  title: z.string().min(1),
  scheduledAt: z.string(),
  // "{datum och tid}", as Hem's card words it.
  dateTimeLabel: z.string().min(1),
  // "{mötets läkare}"; null when the doctor cannot be read.
  doctorName: z.string().nullable(),
  remaining: meetingRouteTaskSchema.nullable(),
  // "Hälsodeklarationen återstår" / the concrete material / "Redo inför
  // vårdmötet".
  status: z.string().min(1),
  // The one primary action, the same as Hem's: the remaining task, else the
  // preparation. The app puts "Anslut till samtalet" in its place once the
  // session is open, and adds "Visa mötesinformation", "Boka om", "Lägg till
  // i kalendern" and "Avboka" itself, as on Hem.
  primary: homeNowActionSchema,
});
export type OnboardingGuideMeeting = z.infer<
  typeof onboardingGuideMeetingSchema
>;

export const onboardingGuideEntrySchema = z.discriminatedUnion("kind", [
  onboardingGuideMessageSchema,
  onboardingGuideTaskEntrySchema,
  onboardingGuideLinkSchema,
  onboardingGuideInfoSchema,
  onboardingGuideStatusSchema,
  onboardingGuideMeetingSchema,
]);
export type OnboardingGuideEntry = z.infer<typeof onboardingGuideEntrySchema>;

// The fixed onboarding sender (env ONBOARDING_GUIDE_DOCTOR_ID): "Läkarens
// verkliga namn, titel och godkända foto används." The title is the app's own role label
// ("Läkare"), as in the care-team header; there is no per-employee title.
// `avatarMediaKey` is resolved by the server (her own portrait, else the
// tenant's doctor slot, else null and the app's neutral silhouette).
export const onboardingGuideSenderSchema = z.object({
  employeeId: z.string(),
  name: z.string(),
  avatarMediaKey: z.string().nullable(),
});
export type OnboardingGuideSender = z.infer<typeof onboardingGuideSenderSchema>;

export const onboardingGuideSchema = z.object({
  sender: onboardingGuideSenderSchema,
  entries: z.array(onboardingGuideEntrySchema).min(1),
});
export type OnboardingGuide = z.infer<typeof onboardingGuideSchema>;

// ---- the negative F-01 feedback (TES-1663) ----------------------------------
//
// "KANONISKT BESLUT 2026-11-02 — NEGATIV F-01-COPY, BMI FRÅN 25 OCH DIREKT
// MÖTESRUTT": "BMI under 25 ger negativ tidig vägledning enligt den kliniskt
// godkända BMI-regeln, med möjlighet att rätta eller uppdatera svar samt boka
// vårdmöte för individuell bedömning." The decision is about the BMI rule, so
// only that stop gets the channel message. The other stops keep their own
// approved pages: type 1 diabetes and MTC/MEN 2 offer no meeting at all
// (§13/§14), and whether pregnancy, breastfeeding and trying get this message
// too is asked as Ö-1060.
export const NEGATIVE_F01_CHANNEL_OUTCOMES: ReadonlySet<F01ResultOutcome> =
  new Set<F01ResultOutcome>(["stop_bmi"]);

// "I din återkoppling såg du att {kort medicinskt godkänd orsak}." — the short
// reason per outcome, which has no approved words yet (Ö-1059). The result
// page's reason sentences (F01_STOP_REASONS) are whole sentences and do not
// fit the clause. Until a reason is approved for an outcome, the message
// leaves that one sentence out rather than invent medical text.
export const NEGATIVE_F01_REASONS: Partial<
  Record<F01ResultOutcome, Record<Locale, string>>
> = {};

// TES-1667 — the booked meeting's card, on Hem and in the channel, verbatim
// from "KANONISK SYNK 2026-11-02". The English is ours. The F-07 wording
// ("{konkret underlag}") has no approved words: it is a proposal built from
// the approved F-07 card title "Uppgifter om din pågående behandling"
// (TES-475 Ö-1064).
export const MEETING_CARD_COPY = {
  sv: {
    title: "Ditt vårdmöte",
    status: {
      health_declaration: "Hälsodeklarationen återstår",
      transfer_details: "Uppgifterna om din pågående behandling återstår",
      ready: "Redo inför vårdmötet",
    },
    cta: {
      health_declaration: "Gör klart hälsodeklarationen",
      transfer_details: "Gör klart uppgifterna om din pågående behandling",
      ready: "Förbered dig inför mötet",
    },
    // "När allt är klart: Läkarmeddelande:".
    ready: [
      "Tack, nu är underlaget inför ditt vårdmöte klart. Du behöver inte göra något mer just nu. Du får en påminnelse inför mötet.",
    ],
  },
  en: {
    title: "Your consultation",
    status: {
      health_declaration: "Health declaration remaining",
      transfer_details: "Details about your ongoing treatment remaining",
      ready: "Ready for your consultation",
    },
    cta: {
      health_declaration: "Finish the health declaration",
      transfer_details: "Finish the details about your ongoing treatment",
      ready: "Prepare for your consultation",
    },
    ready: [
      "Thank you, everything is now in for your consultation. You do not need to do anything more right now. You will get a reminder before the meeting.",
    ],
  },
} as const satisfies Record<Locale, unknown>;

// ---- the approved copy -------------------------------------------------------
//
// "Ditt medicinska team — chattkort och vårdrollscopy" (Emelie 2026-10-29/30),
// verbatim. The English is ours, a copy proposal (TES-475 Ö-1045).

type Name = string | null;

// "Hej {förnamn}, …" / "Tack {förnamn} …" without a first name on file: the
// name and the space before it go, as Hem's "Tack, nu är din
// hälsodeklaration inskickad." does.
const named = (lead: string, name: Name, rest: string) =>
  name ? `${lead} ${name}${rest}` : `${lead}${rest}`;

export const ONBOARDING_GUIDE_COPY = {
  sv: {
    // "GODKÄND COPY 2026-10-30 — LÄKARENS VÄLKOMSTMEDDELANDE FÖRE F-01", with
    // its introduction in the later words of Ö-1044 (2026-11-02): "Jag heter
    // {namn} och är läkare här på Ella. Jag guidar dig genom de första
    // stegen …". It names no relation to her case.
    welcome: (firstName: Name, doctorName: string) => [
      named("Hej", firstName, ", varmt välkommen till Ella!"),
      `Jag heter ${doctorName} och är läkare här på Ella. Jag guidar dig genom de första stegen, så att du vet vad du behöver göra och vad som händer sedan.`,
      "Vi börjar med några korta frågor om dig, din hälsa och din viktresa. Utifrån dina svar får du återkoppling direkt och veta vilket ditt nästa steg är. Du behöver inte förbereda några dokument eller mätningar nu.",
    ],
    f01Card: {
      title: "Din första kartläggning",
      text: "Några korta frågor om dig och din viktresa.",
      cta: "Börja kartläggningen",
    },
    // "GODKÄND COPY OCH RESTPUNKT 2026-10-30 — EFTER POSITIVT F-01".
    afterPositiveF01: (firstName: Name) => [
      named("Tack", firstName, " för att du har berättat mer om dig!"),
      "Dina svar visar att du kan gå vidare till nästa steg hos Ella. Nu behöver du fylla i din hälsodeklaration. Där berättar du mer om din hälsa och vilka läkemedel du använder, så att jag får underlag för den medicinska bedömningen.",
    ],
    // "GODKÄND COPY 2026-10-30 — RECEPTÖVERTAG EFTER F-01".
    afterTransferF01: (firstName: Name) => [
      named("Tack", firstName, " för att du har berättat mer om dig!"),
      "Du har angett att du redan har en pågående läkemedelsbehandling och vill fortsätta hos oss på Ella.",
      "För att jag ska kunna bedöma om vi kan ta över din behandling behöver du först fylla i din hälsodeklaration. Där berättar du mer om din hälsa och vilka läkemedel du använder.",
    ],
    // Both variants' card "KORT DIREKT EFTER MEDDELANDET" (the same words as
    // Hem's declaration card on the optional track).
    declarationCard: {
      title: "Din hälsodeklaration",
      text: "Frågor om din hälsa och dina läkemedel.",
      cta: "Fyll i hälsodeklarationen",
    },
    // "Se din första kartläggning leder till patientens sparade objekt i Din
    // resa".
    f01EntryLink: "Se din första kartläggning",
    // "GODKÄND COPY 2026-10-30 — LÄKARENS INTRODUKTION INFÖR F-07".
    beforeF07: (firstName: Name) => [
      named("Tack", firstName, ", nu är din hälsodeklaration inskickad."),
      "Nästa steg är att berätta mer om din pågående behandling – vilket läkemedel du använder, din dos och hur behandlingen har fungerat för dig.",
      "I samma formulär laddar du också upp ett foto eller en PDF av ditt aktuella recept. Det behöver jag för att kunna bedöma om vi kan ta över din behandling.",
    ],
    transferCard: {
      title: "Uppgifter om din pågående behandling",
      text: "Frågor om behandlingen och uppladdning av receptunderlag.",
      cta: "Fortsätt",
    },
    // "GODKÄND INFORMATIONSTEXT 2026-11-02 — SÅ GÅR LÄKARBEDÖMNINGEN TILL",
    // verbatim. It describes the new-treatment path (the choice between a
    // written assessment and a meeting), so only that path shows it.
    assessmentInfo: {
      title: "Så går läkarbedömningen till",
      paragraphs: [
        "När du har fyllt i din hälsodeklaration väljer du om du vill fortsätta med en skriftlig bedömning eller boka ett digitalt vårdmöte.",
        "Därefter lämnar du de underlag som gäller för dig. När allt är inskickat går en läkare igenom helheten och lämnar sitt besked i appen.",
        "Vissa svar eller medicinska förutsättningar kan innebära att läkaren behöver träffa dig i ett digitalt vårdmöte innan bedömningen kan slutföras. Om Ella bedömer att ett vårdmöte behövs för din medicinska bedömning kostar mötet inget extra.",
        "Om läkaren behöver andra uppgifter får du en tydlig kompletteringsuppgift i appen.",
      ],
    },
    // Ö-1044 "ÖVERLÄMNING", verbatim.
    handover: (doctorName: string) =>
      `Nu är det ${doctorName}, läkare på Ella, som går igenom ditt underlag och ansvarar för din medicinska bedömning.`,
    // TES-1663 — "NEGATIV KANALCOPY — GODKÄND", verbatim. `reason` is the
    // "{kort medicinskt godkänd orsak}" (NEGATIVE_F01_REASONS); null leaves
    // its sentence out.
    negativeF01: (firstName: Name, reason: string | null) => [
      named("Hej", firstName, "."),
      `Tack för att du har svarat på våra första frågor.${reason ? ` I din återkoppling såg du att ${reason}.` : ""} Därför ser det just nu ut som att Ella sannolikt inte kan erbjuda dig läkemedelsbehandling.`,
      "Det här är en tidig vägledning utifrån de svar du har lämnat, inte ett slutligt individuellt läkarbeslut. Om något svar blev fel eller din situation har förändrats kan du uppdatera dina svar och få en ny återkoppling. Uppgifterna behöver alltid beskriva din aktuella situation korrekt.",
      "Om du vill få en individuell läkarbedömning och möjlighet att ställa frågor kan du också boka ett digitalt vårdmöte. Ett vårdmöte innebär inte att behandling kan garanteras och kan genomföras av en annan läkare än onboardingläkaren.",
    ],
    // Its "Handlingar". The fee is the one the API charges (CONSULT_FEE_CENTS,
    // "395 kr"); without one the label names no amount.
    negativeF01Actions: {
      updateAnswers: "Uppdatera mina svar",
      readMore: "Läs mer om beskedet",
      book: (fee: string | null) =>
        fee ? `Boka vårdmöte – ${fee}` : "Boka vårdmöte",
      closeForNow: "Avsluta för nu",
    },
    // "HÄLSODEKLATION OCH KORT" — "Läkarens meddelande efter bokning" and
    // its card, verbatim, after a meeting booked from the negative result.
    afterNegativeBooking: [
      "Tack, ditt vårdmöte är bokat. För att läkaren ska kunna förbereda sig behöver du nu fylla i din hälsodeklaration och lämna de underlag som gäller för dig. Du ser här i appen vad som behöver vara klart före mötet.",
    ],
    meetingDeclarationCard: {
      title: "Din hälsodeklaration",
      text: "Svara på frågor om din hälsa inför vårdmötet.",
      cta: "Fyll i hälsodeklarationen",
    },
  },
  en: {
    welcome: (firstName: Name, doctorName: string) => [
      named("Hi", firstName, ", and a warm welcome to Ella!"),
      `My name is ${doctorName} and I am a doctor here at Ella. I will guide you through the first steps, so that you know what you need to do and what happens next.`,
      "We start with a few short questions about you, your health and your weight journey. Based on your answers you get feedback straight away and find out what your next step is. You do not need to prepare any documents or measurements now.",
    ],
    f01Card: {
      title: "Your first assessment",
      text: "A few short questions about you and your weight journey.",
      cta: "Start the assessment",
    },
    afterPositiveF01: (firstName: Name) => [
      named("Thank you", firstName, " for telling us more about yourself!"),
      "Your answers show that you can go on to the next step with Ella. Now you need to fill in your health declaration. There you tell us more about your health and the medicines you use, so that I have what I need for the medical assessment.",
    ],
    afterTransferF01: (firstName: Name) => [
      named("Thank you", firstName, " for telling us more about yourself!"),
      "You have told us that you already have an ongoing medication treatment and want to continue it with us at Ella.",
      "For me to be able to assess whether we can take over your treatment, you first need to fill in your health declaration. There you tell us more about your health and the medicines you use.",
    ],
    declarationCard: {
      title: "Your health declaration",
      text: "Questions about your health and your medicines.",
      cta: "Fill in the health declaration",
    },
    f01EntryLink: "See your first assessment",
    beforeF07: (firstName: Name) => [
      named("Thank you", firstName, ", your health declaration has been sent."),
      "The next step is to tell us more about your ongoing treatment – which medicine you use, your dose and how the treatment has worked for you.",
      "In the same form you also upload a photo or a PDF of your current prescription. I need it to be able to assess whether we can take over your treatment.",
    ],
    transferCard: {
      title: "Details about your ongoing treatment",
      text: "Questions about the treatment and upload of your prescription.",
      cta: "Continue",
    },
    assessmentInfo: {
      title: "How the doctor's assessment works",
      paragraphs: [
        "Once you have filled in your health declaration, you choose whether to continue with a written assessment or book a digital consultation.",
        "After that you provide the information that applies to you. When everything is in, a doctor goes through the whole picture and gives you an answer in the app.",
        "Some answers or medical circumstances may mean that the doctor needs to see you in a digital consultation before the assessment can be completed. If Ella judges that a consultation is needed for your medical assessment, it costs nothing extra.",
        "If the doctor needs other information, you get a clear completion task in the app.",
      ],
    },
    handover: (doctorName: string) =>
      `Now ${doctorName}, a doctor at Ella, is going through your information and is responsible for your medical assessment.`,
    negativeF01: (firstName: Name, reason: string | null) => [
      named("Hi", firstName, "."),
      `Thank you for answering our first questions.${reason ? ` In your feedback you saw that ${reason}.` : ""} So at the moment it looks as if Ella is unlikely to be able to offer you medication.`,
      "This is early guidance based on the answers you have given, not a final individual decision by a doctor. If an answer was wrong or your situation has changed, you can update your answers and get new feedback. Your details always need to describe your current situation correctly.",
      "If you want an individual assessment by a doctor and a chance to ask questions, you can also book a digital consultation. A consultation does not mean that treatment can be guaranteed, and it may be held by a different doctor from the onboarding doctor.",
    ],
    negativeF01Actions: {
      updateAnswers: "Update my answers",
      readMore: "Read more about the feedback",
      book: (fee: string | null) =>
        fee ? `Book a consultation – ${fee}` : "Book a consultation",
      closeForNow: "Close for now",
    },
    afterNegativeBooking: [
      "Thank you, your consultation is booked. So that the doctor can prepare, you now need to fill in your health declaration and provide the information that applies to you. Here in the app you can see what needs to be done before the meeting.",
    ],
    meetingDeclarationCard: {
      title: "Your health declaration",
      text: "Answer questions about your health before the consultation.",
      cta: "Fill in the health declaration",
    },
  },
} as const satisfies Record<Locale, unknown>;
